'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { HolidayImage } from '@/src/types/holidays'

import ImageCarousel from './imageCarousel'

interface ImageCarouselWrapperProps {
    images: HolidayImage[]
}

const SLIDE_MS = 3500

const ImageCarouselWrapper: React.FC<ImageCarouselWrapperProps> = ({ images }) => {
    const trackRef = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    // The timer reads the current slide without re-subscribing every time it changes, which would
    // restart the interval on each tick and make the last slide linger.
    const indexRef = useRef(0)

    const goTo = useCallback((index: number) => {
        const track = trackRef.current
        indexRef.current = index
        setActiveIndex(index)
        if (track) track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' })
    }, [])

    useEffect(() => {
        if (images.length < 2) return undefined

        // A collapsed day unmounts its panel, so no timer runs for a day nobody is reading.
        const timer = window.setInterval(() => {
            const track = trackRef.current
            const next = (indexRef.current + 1) % images.length
            indexRef.current = next
            setActiveIndex(next)
            if (track) track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' })
        }, SLIDE_MS)

        return () => window.clearInterval(timer)
    }, [images.length])

    // A swipe moves the track without going through `goTo`, so the dots follow the scroll position.
    const handleScroll = useCallback(() => {
        const track = trackRef.current
        if (!track?.clientWidth) return

        // `scrollLeft` runs negative in RTL, where the slides are laid out right to left.
        const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth)
        indexRef.current = index
        setActiveIndex(index)
    }, [])

    if (!images.length) return null

    return (
        <ImageCarousel
            images={images}
            activeIndex={activeIndex}
            trackRef={trackRef}
            onScroll={handleScroll}
            onSelect={goTo}
        />
    )
}

export { ImageCarouselWrapper }
export default ImageCarouselWrapper
